/**
 * Lista template newsletter (RF-03b / §8.4).
 *
 * Due gruppi logici (§11.7):
 *  - Template di entità  → entity_id valorizzato (CRUD con NEWSLETTER W sull'entità).
 *  - Template standard    → entity_id = null (gestibili solo da admin nazionale; selezionabili da tutti).
 *
 * DELETE = archiviazione (is_archived = true): l'azione mostra "Archivia", non "Elimina".
 * Il toggle "Mostra archiviati" ricarica la lista con is_archived=true (sola consultazione).
 *
 * Riferimento: modellata su Filters.js. TDD Sprint 18 §8.4.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ThemeProvider } from '@mui/material/styles';
import {
  Card,
  Container,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableContainer,
  TableCell,
  TableFooter,
  TablePagination,
  Stack,
  Typography,
  Chip,
  Tooltip,
  InputAdornment,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
//--------------------------------------------------------
import {
  CustomIconButton,
  DefaultContainedButton,
  DefaultOutlinedButton,
  GreenOutlinedButton,
  RedOutlinedButton,
} from '../../../../utils/Buttons';
import { CustomTextField } from '../../../../utils/StyledInputs';
import { TablePaginationActions, theme } from '../../../../utils/TableStyles';
import { DecisionDialog } from '../../../../utils/DecisionDialog';
import { ErrorUI, HttpErrorHandler } from '../../../../utils/ErrorHandler';
import { usePopup } from '../../../../context/PopupContext';
import { templatesApi } from '../../../../services/newsletter/templatesApi';

const PER_PAGE = 10;
/** Attesa prima di interrogare il BE mentre si digita nella ricerca. */
const SEARCH_DEBOUNCE_MS = 500;

/** Normalizza la risposta (paginata o array) in un array di template. */
function extractTemplates(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.templates)) return data.templates;
  return [];
}

export default function NewsletterTemplates() {
  const navigate = useNavigate();
  const { showPopup, setPopUpText } = usePopup();

  const [templates, setTemplates] = useState([]);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(PER_PAGE);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const [showArchived, setShowArchived] = useState(false);
  const [page, setPage] = useState(0); // 0-based

  // Ricerca: `searchQuery` è il valore digitato, `search` quello inviato al BE (debounced).
  const [searchQuery, setSearchQuery] = useState('');
  const [search, setSearch] = useState('');
  const searchTimerRef = useRef(null);

  // Caricamento lista (server-side: paginazione, stato archiviazione, ricerca).
  const fetchTemplates = useCallback((pageIndex, archived, searchTerm) => {
    const params = { page: pageIndex + 1, is_archived: archived };
    if (searchTerm) params.search = searchTerm;

    setLoading(true);
    setErrorMessage('');
    templatesApi
      .list(params)
      .then((res) => {
        const d = res.data ?? {};
        setTemplates(extractTemplates(d));
        setTotal(d.total ?? (Array.isArray(d) ? d.length : 0));
        if (d.per_page) setPerPage(d.per_page);
      })
      .catch((err) => {
        HttpErrorHandler(err, setErrorMessage);
        setTemplates([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchTemplates(page, showArchived, search);
  }, [page, showArchived, search, fetchTemplates]);

  // Cleanup del timer di debounce allo smontaggio
  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  // Digitazione nella ricerca: aggiorna subito l'input, interroga il BE dopo il debounce.
  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchQuery(value);

    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(0);
      setSearch(value.trim());
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleToggleArchived = (e) => {
    setPage(0);
    setShowArchived(e.target.checked);
  };

  // -------------------- Navigazione --------------------
  const handleCreate = () => navigate('/admin/newsletter/templates/detail', { state: { mode: 'create' } });
  const handleEdit = (t) =>
    navigate('/admin/newsletter/templates/detail', { state: { mode: 'edit', templateId: t.id } });

  // -------------------- Archiviazione --------------------
  const [archiveDialog, setArchiveDialog] = useState({ open: false, template: null });

  const handleArchive = () => {
    const { template } = archiveDialog;
    templatesApi
      .toggleArchive(template.id)
      .then((res) => {
        showPopup(true, res.data?.message || 'Template archiviato');
        fetchTemplates(page, showArchived, search);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 403) {
          showPopup(false, 'Non hai i permessi per archiviare questo template');
        } else {
          HttpErrorHandler(err, setPopUpText);
          showPopup(false);
        }
      })
      .finally(() => setArchiveDialog({ open: false, template: null }));
  };

  // -------------------- Disarchiviazione (stesso endpoint toggle) --------------------
  const [unarchiveDialog, setUnarchiveDialog] = useState({ open: false, template: null });

  const handleUnarchive = () => {
    const { template } = unarchiveDialog;
    templatesApi
      .toggleArchive(template.id)
      .then((res) => {
        showPopup(true, res.data?.message || 'Template ripristinato');
        fetchTemplates(page, showArchived, search);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 403) {
          showPopup(false, 'Non hai i permessi per disarchiviare questo template');
        } else {
          HttpErrorHandler(err, setPopUpText);
          showPopup(false);
        }
      })
      .finally(() => setUnarchiveDialog({ open: false, template: null }));
  };

  const scopeLabel = (t) =>
    t.entity_id ? t.entity?.name ?? 'Entità' : 'Standard';

  return (
    <>
      <Helmet>
        <title>Template Newsletter</title>
      </Helmet>

      <Container maxWidth="xl">
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <DefaultOutlinedButton onClick={() => navigate('/admin/newsletter')} startIcon={<ArrowBackIcon />}>
              Indietro
            </DefaultOutlinedButton>
            <Typography variant="h4">Template Newsletter</Typography>
          </Stack>
          <DefaultContainedButton onClick={handleCreate} startIcon={<AddIcon />}>
            Nuovo Template
          </DefaultContainedButton>
        </Stack>

        {errorMessage?.length > 0 ? (
          ErrorUI(errorMessage)
        ) : (
          <Card>
            {/* Toolbar: ricerca + toggle archiviati */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              spacing={2}
              sx={{ px: 2, pt: 2 }}
            >
              <CustomTextField
                size="small"
                placeholder="Cerca template per nome o entità"
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#637381' }} />
                    </InputAdornment>
                  ),
                }}
                customStyles={{ maxWidth: 400 }}
              />
              <FormControlLabel
                control={<Switch checked={showArchived} onChange={handleToggleArchived} />}
                label="Mostra archiviati"
              />
            </Stack>

            <TableContainer sx={{ padding: '24px' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell align="center">Ambito</TableCell>
                    <TableCell>Oggetto</TableCell>
                    <TableCell align="center">Stato</TableCell>
                    <TableCell align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>

                {loading ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ p: 2 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : templates.length === 0 ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        {search ? `Nessun template trovato per "${search}"` : 'Nessun template trovato'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  <TableBody>
                    {templates.map((t) => (
                      <TableRow
                        key={t.id}
                        hover
                        sx={{ cursor: 'pointer', height: '53px' }}
                        onClick={() => handleEdit(t)}
                      >
                        <TableCell>{t.name}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={scopeLabel(t)}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              backgroundColor: t.entity_id ? '#e3f2fd' : '#ede7f6',
                              color: t.entity_id ? '#1565c0' : '#5e35b1',
                            }}
                          />
                        </TableCell>
                        <TableCell>{t.subject || '—'}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={t.is_archived ? 'Archiviato' : 'Attivo'}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              backgroundColor: t.is_archived ? '#fff3e0' : '#e8f5e9',
                              color: t.is_archived ? '#e65100' : '#2e7d32',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" justifyContent="flex-end" spacing={0}>
                            <Tooltip title="Modifica">
                              <CustomIconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(t);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </CustomIconButton>
                            </Tooltip>
                            {t.is_archived ? (
                              <Tooltip title="Disarchivia">
                                <CustomIconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setUnarchiveDialog({ open: true, template: t });
                                  }}
                                >
                                  <UnarchiveIcon fontSize="small" />
                                </CustomIconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Archivia">
                                <CustomIconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setArchiveDialog({ open: true, template: t });
                                  }}
                                >
                                  <ArchiveIcon fontSize="small" />
                                </CustomIconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                )}

                {total > perPage && (
                  <TableFooter>
                    <TableRow>
                      <ThemeProvider theme={theme}>
                        <TablePagination
                          sx={{ border: 0 }}
                          rowsPerPageOptions={[]}
                          colSpan={5}
                          count={total}
                          rowsPerPage={perPage}
                          page={page}
                          onPageChange={handleChangePage}
                          labelRowsPerPage="Righe per Pagina"
                          ActionsComponent={TablePaginationActions}
                        />
                      </ThemeProvider>
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </TableContainer>
          </Card>
        )}
      </Container>

      <DecisionDialog
        openDialog={archiveDialog.open}
        dialogTitle="Archivia template"
        dialogDescription={`Sei sicuro di voler archiviare il template "${archiveDialog.template?.name ?? ''}"? Non sarà più selezionabile in composizione.`}
        closeOperation={() => setArchiveDialog({ open: false, template: null })}
        requestOperation={handleArchive}
        requestText="Archivia"
        RequestButton={RedOutlinedButton}
        closeText="Annulla"
      />

      <DecisionDialog
        openDialog={unarchiveDialog.open}
        dialogTitle="Disarchivia template"
        dialogDescription={`Sei sicuro di voler disarchiviare il template "${unarchiveDialog.template?.name ?? ''}"? Tornerà selezionabile in composizione.`}
        closeOperation={() => setUnarchiveDialog({ open: false, template: null })}
        requestOperation={handleUnarchive}
        requestText="Disarchivia"
        RequestButton={GreenOutlinedButton}
        closeText="Annulla"
      />
    </>
  );
}
